import React, { useMemo, useState } from 'react';
import { HiOutlineX, HiOutlineDownload } from 'react-icons/hi';
import axiosInstance from '../../../api/axiosConfig';
import './ImportWizard.css';

/**
 * Generic Odoo-style import wizard: upload a file → map its columns to a
 * caller-supplied field schema → dry-run check → confirm.
 *
 * This component knows nothing about "customers" or any other domain — it is
 * driven entirely by the `urls` and the field schema the server hands back
 * after upload. A future module can reuse it by pointing at its own
 * preview/validate/confirm/template endpoints on the backend's import
 * wizard engine (see backend utils/importWizard.js).
 *
 * Props:
 *   title        - modal heading, e.g. "Import Customers"
 *   urls.upload    - POST endpoint, multipart 'file' field
 *   urls.template  - GET endpoint returning a template file (optional)
 *   urls.validate(id) - POST endpoint, body { mapping }
 *   urls.confirm(id)  - POST endpoint, body { mapping }
 *   onClose()
 *   onImported(result) - called after a successful confirm, so the caller can refresh its list
 */
const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;

const downloadCsvFromErrors = (errors, filename) => {
    const header = ['Row', 'Reason', ...Object.keys(errors[0]?.data || {})];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [header.map(escape).join(',')];
    errors.forEach((e) => {
        const row = [e.row, e.reason, ...Object.keys(errors[0]?.data || {}).map((k) => e.data?.[k])];
        lines.push(row.map(escape).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

const ImportWizard = ({ title = 'Import', urls, onClose, onImported }) => {
    const [step, setStep] = useState('upload'); // upload | map | checked | done
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null); // { id, headers, sampleRows, fields, suggestedMapping, rowCount, filename }
    const [mapping, setMapping] = useState({});
    const [checkResult, setCheckResult] = useState(null);
    const [confirmResult, setConfirmResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const fields = preview?.fields || [];

    const usedFieldsByHeader = useMemo(() => {
        const map = {};
        (preview?.headers || []).forEach((h) => {
            map[h] = new Set(
                Object.entries(mapping)
                    .filter(([header, fieldKey]) => header !== h && fieldKey)
                    .map(([, fieldKey]) => fieldKey)
            );
        });
        return map;
    }, [preview, mapping]);

    const requiredFieldsMapped = useMemo(() => {
        const mappedKeys = new Set(Object.values(mapping).filter(Boolean));
        return fields.filter((f) => f.required).every((f) => mappedKeys.has(f.key));
    }, [fields, mapping]);

    const handleUpload = async () => {
        if (!file) return;
        setBusy(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axiosInstance.post(urls.upload, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const data = res.data.data;
            setPreview(data);
            setMapping(data.suggestedMapping || {});
            setStep('map');
        } catch (err) {
            setError(errorMessage(err, 'Could not read that file'));
        } finally {
            setBusy(false);
        }
    };

    const handleDownloadTemplate = async () => {
        if (!urls.template) return;
        try {
            const res = await axiosInstance.get(urls.template, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'import-template.xlsx';
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(errorMessage(err, 'Could not download the template'));
        }
    };

    const handleCheck = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await axiosInstance.post(urls.validate(preview.id), { mapping });
            setCheckResult(res.data.data);
            setStep('checked');
        } catch (err) {
            setError(errorMessage(err, 'Could not check that file'));
        } finally {
            setBusy(false);
        }
    };

    const handleConfirm = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await axiosInstance.post(urls.confirm(preview.id), { mapping });
            setConfirmResult(res.data.data);
            setStep('done');
            onImported && onImported(res.data.data);
        } catch (err) {
            setError(errorMessage(err, 'Could not import that file'));
        } finally {
            setBusy(false);
        }
    };

    const sampleFor = (header) => {
        const row = (preview?.sampleRows || []).find((r) => String(r[header] ?? '').trim() !== '');
        return row ? String(row[header]) : '';
    };

    return (
        <div className="iw-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
            <div className="iw-modal" role="dialog" aria-modal="true">
                <div className="iw-head">
                    <h2>{title}</h2>
                    <button className="iw-close" onClick={onClose} disabled={busy} aria-label="Close">
                        <HiOutlineX />
                    </button>
                </div>

                {step === 'upload' && (
                    <div className="iw-body">
                        <div className="iw-field">
                            <label htmlFor="iw-file">CSV or Excel file</label>
                            <input
                                id="iw-file"
                                type="file"
                                accept=".csv,.xls,.xlsx"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                            />
                            <span className="iw-hint">.csv, .xls or .xlsx · columns can be in any order</span>
                        </div>
                        {urls.template && (
                            <button type="button" className="iw-link" onClick={handleDownloadTemplate}>
                                <HiOutlineDownload /> Download a blank template
                            </button>
                        )}
                        <div className="iw-actions">
                            <button className="iw-btn" onClick={onClose} disabled={busy}>Cancel</button>
                            <button className="iw-btn iw-btn-primary" onClick={handleUpload} disabled={!file || busy}>
                                {busy ? 'Reading file…' : 'Next: map columns'}
                            </button>
                        </div>
                    </div>
                )}

                {step === 'map' && preview && (
                    <div className="iw-body">
                        <p className="iw-summary">
                            <strong>{preview.filename}</strong> · {preview.rowCount.toLocaleString()} rows
                        </p>
                        <div className="iw-map-table-wrap">
                            <table className="iw-map-table">
                                <thead>
                                    <tr>
                                        <th>File column</th>
                                        <th>Sample value</th>
                                        <th>Maps to</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.headers.map((header) => (
                                        <tr key={header}>
                                            <td>{header}</td>
                                            <td className="iw-sample">{sampleFor(header)}</td>
                                            <td>
                                                <select
                                                    value={mapping[header] || ''}
                                                    onChange={(e) =>
                                                        setMapping((prev) => ({ ...prev, [header]: e.target.value }))
                                                    }
                                                >
                                                    <option value="">— Do not import —</option>
                                                    {fields.map((f) => (
                                                        <option
                                                            key={f.key}
                                                            value={f.key}
                                                            disabled={usedFieldsByHeader[header]?.has(f.key)}
                                                        >
                                                            {f.label}{f.required ? ' *' : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {!requiredFieldsMapped && (
                            <div className="iw-alert iw-alert-warn">
                                Map all required fields (*) before checking the file.
                            </div>
                        )}
                        <div className="iw-actions">
                            <button className="iw-btn" onClick={() => setStep('upload')} disabled={busy}>Back</button>
                            <button
                                className="iw-btn iw-btn-primary"
                                onClick={handleCheck}
                                disabled={busy || !requiredFieldsMapped}
                            >
                                {busy ? 'Checking…' : 'Check file'}
                            </button>
                        </div>
                    </div>
                )}

                {step === 'checked' && checkResult && (
                    <div className="iw-body">
                        <dl className="iw-check-facts">
                            <dt>Will create</dt><dd>{checkResult.created.toLocaleString()}</dd>
                            <dt>Will update</dt><dd>{checkResult.updated.toLocaleString()}</dd>
                            <dt>Will skip (errors)</dt><dd>{checkResult.failed.toLocaleString()}</dd>
                        </dl>
                        {checkResult.errors.length > 0 && (
                            <>
                                <div className="iw-alert iw-alert-warn">
                                    {checkResult.failed} row(s) have errors and will be skipped. They will not block
                                    importing the rest.
                                </div>
                                <details className="iw-details" open={checkResult.errors.length <= 10}>
                                    <summary>Show error rows</summary>
                                    <div className="iw-table-wrap">
                                        <table className="iw-table">
                                            <thead><tr><th>Row</th><th>Reason</th></tr></thead>
                                            <tbody>
                                                {checkResult.errors.slice(0, 200).map((e) => (
                                                    <tr key={e.row}><td>{e.row}</td><td>{e.reason}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </details>
                            </>
                        )}
                        <div className="iw-actions">
                            <button className="iw-btn" onClick={() => setStep('map')} disabled={busy}>Back to mapping</button>
                            <button
                                className="iw-btn iw-btn-primary"
                                onClick={handleConfirm}
                                disabled={busy || (checkResult.created === 0 && checkResult.updated === 0)}
                            >
                                {busy ? 'Importing…' : `Import ${checkResult.created + checkResult.updated} row(s)`}
                            </button>
                        </div>
                    </div>
                )}

                {step === 'done' && confirmResult && (
                    <div className="iw-body">
                        <div className="iw-alert iw-alert-ok">
                            ✓ {confirmResult.created.toLocaleString()} created ·{' '}
                            {confirmResult.updated.toLocaleString()} updated
                            {confirmResult.failed > 0 && ` · ${confirmResult.failed.toLocaleString()} skipped`}
                        </div>
                        {confirmResult.errors.length > 0 && (
                            <button
                                type="button"
                                className="iw-link"
                                onClick={() => downloadCsvFromErrors(confirmResult.errors, 'import-errors.csv')}
                            >
                                <HiOutlineDownload /> Download skipped-row report
                            </button>
                        )}
                        <div className="iw-actions">
                            <button className="iw-btn iw-btn-primary" onClick={onClose}>Done</button>
                        </div>
                    </div>
                )}

                {error && <div className="iw-alert iw-alert-error">{error}</div>}
            </div>
        </div>
    );
};

export default ImportWizard;
